import { useEffect, useState } from "react";
import { api, API, money, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { FileDown, Undo2, Ban } from "lucide-react";

export default function Returns() {
  const { can } = useAuth();
  const [notes, setNotes] = useState([]);
  const [txns, setTxns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [ret, setRet] = useState(null); // {source, doc}
  const [voidTx, setVoidTx] = useState(null);
  const [voidReason, setVoidReason] = useState("");

  const load = () => {
    api.get("/credit-notes").then(({ data }) => setNotes(data)).catch(() => {});
    api.get("/pos/transactions").then(({ data }) => setTxns(data.slice(0, 200))).catch(() => {});
    api.get("/invoices").then(({ data }) => setInvoices(data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/returns/reasons").then(({ data }) => setReasons(data)).catch(() => {}); }, []);

  const doVoid = async () => {
    try {
      await api.post(`/pos/transactions/${voidTx.id}/void`, { reason: voidReason });
      toast.success(`${voidTx.number} voided and stock restored`);
      setVoidTx(null); setVoidReason(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Returns & Credit Notes" subtitle={`${notes.length} credit notes issued`} />
      <Tabs defaultValue="notes">
        <TabsList data-testid="returns-tabs">
          <TabsTrigger value="notes" data-testid="tab-credit-notes">Credit Notes</TabsTrigger>
          <TabsTrigger value="pos" data-testid="tab-pos-returns">POS Sales</TabsTrigger>
          <TabsTrigger value="inv" data-testid="tab-invoice-returns">Invoices</TabsTrigger>
        </TabsList>

        <TabsContent value="notes" className="bg-white border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-secondary hover:bg-secondary"><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Against</TableHead><TableHead>Customer</TableHead><TableHead>Reason</TableHead><TableHead>Refund</TableHead><TableHead className="text-right">Total</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {notes.map((n) => (
                <TableRow key={n.id} data-testid={`credit-note-row-${n.number}`}>
                  <TableCell className="font-mono text-sm">{n.number}</TableCell>
                  <TableCell className="text-sm">{fmtDate(n.date)}</TableCell>
                  <TableCell className="font-mono text-xs">{n.ref_number}</TableCell>
                  <TableCell>{n.customer_name}</TableCell>
                  <TableCell><Badge variant="outline">{n.reason}</Badge></TableCell>
                  <TableCell className="capitalize text-sm">{n.refund_method}</TableCell>
                  <TableCell className="text-right tabular font-semibold text-primary">{money(n.total)}</TableCell>
                  <TableCell><a href={`${API}/credit-notes/${n.id}/pdf`} target="_blank" rel="noreferrer"><Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`credit-note-pdf-${n.number}`}><FileDown className="h-4 w-4" /></Button></a></TableCell>
                </TableRow>
              ))}
              {notes.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No credit notes yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="pos" className="bg-white border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-secondary hover:bg-secondary"><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead>Operator</TableHead><TableHead>Items</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {txns.map((t) => (
                <TableRow key={t.id} data-testid={`pos-txn-row-${t.number}`}>
                  <TableCell className="font-mono text-sm">{t.number}</TableCell>
                  <TableCell className="text-sm">{fmtDate(t.created_at)}</TableCell>
                  <TableCell className="text-sm">{t.operator_name}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{t.items?.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</TableCell>
                  <TableCell className="text-right tabular">{money(t.total)}</TableCell>
                  <TableCell>{t.voided ? <Badge className="bg-black">Voided</Badge> : t.returned_total ? <Badge variant="outline" className="border-primary text-primary">Returned {money(t.returned_total)}</Badge> : <Badge variant="outline">Complete</Badge>}</TableCell>
                  <TableCell className="text-right">
                    {can("pos") && !t.voided && <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => setRet({ source: "pos", doc: t })} data-testid={`return-pos-${t.number}`}><Undo2 className="h-3.5 w-3.5 mr-1" /> Return</Button>
                      {!t.returned_total && <Button size="sm" variant="ghost" className="h-8 text-muted-foreground hover:text-primary" onClick={() => setVoidTx(t)} data-testid={`void-pos-${t.number}`}><Ban className="h-3.5 w-3.5 mr-1" /> Void</Button>}
                    </div>}
                  </TableCell>
                </TableRow>
              ))}
              {txns.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-12 text-muted-foreground">No POS sales.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>

        <TabsContent value="inv" className="bg-white border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow className="bg-secondary hover:bg-secondary"><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Credited</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {invoices.map((i) => (
                <TableRow key={i.id} data-testid={`inv-return-row-${i.number}`}>
                  <TableCell className="font-mono text-sm">{i.number}</TableCell>
                  <TableCell className="text-sm">{fmtDate(i.date)}</TableCell>
                  <TableCell>{i.customer?.company_name}</TableCell>
                  <TableCell className="text-right tabular">{money(i.total)}</TableCell>
                  <TableCell className="tabular text-sm text-primary">{i.returned_total ? money(i.returned_total) : "—"}</TableCell>
                  <TableCell className="text-right">{can("pos") && <Button size="sm" variant="outline" className="h-8" onClick={() => setRet({ source: "invoice", doc: i })} data-testid={`return-inv-${i.number}`}><Undo2 className="h-3.5 w-3.5 mr-1" /> Return</Button>}</TableCell>
                </TableRow>
              ))}
              {invoices.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No invoices.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TabsContent>
      </Tabs>

      {ret && <ReturnDialog ret={ret} reasons={reasons} onClose={() => setRet(null)} onDone={() => { setRet(null); load(); }} />}

      <Dialog open={!!voidTx} onOpenChange={() => setVoidTx(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-xl">Void {voidTx?.number}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Voiding cancels the sale, returns all items to stock and removes it from the cash-up.</p>
          <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Reason</Label><Input value={voidReason} onChange={(e) => setVoidReason(e.target.value)} placeholder="e.g. Rang up wrong cylinder" data-testid="void-reason-input" /></div>
          <DialogFooter><Button variant="outline" onClick={() => setVoidTx(null)}>Cancel</Button><Button onClick={doVoid} disabled={!voidReason.trim()} className="bg-black hover:bg-zinc-800 text-white" data-testid="confirm-void-button">Void Sale</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ReturnDialog({ ret, reasons, onClose, onDone }) {
  const { source, doc } = ret;
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [refund, setRefund] = useState(source === "invoice" ? "credit" : "cash");
  const [restock, setRestock] = useState(true);
  const items = (doc.items || []).map((i, idx) => ({ ...i, key: i.product_id || `${idx}`, name: i.name || i.description }));
  const total = items.reduce((s, i) => s + (Number(qty[i.key]) || 0) * Number(i.unit_price || 0), 0);
  const shown = source === "pos" ? total : total * 1.15;

  const submit = async () => {
    const lines = items.filter((i) => Number(qty[i.key]) > 0).map((i) => ({ product_id: i.product_id, name: i.name, quantity: Number(qty[i.key]), unit_price: i.unit_price }));
    if (!lines.length) return toast.error("Enter a quantity to return");
    if (!reason) return toast.error("Select a reason");
    try {
      const { data } = await api.post("/returns", { source, ref_id: doc.id, items: lines, reason, refund_method: refund, restock });
      toast.success(`Credit note ${data.number} issued`);
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-xl">Return against {doc.number}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="border border-border rounded-md divide-y divide-border">
            {items.map((i) => (
              <div key={i.key} className="flex items-center gap-3 px-3 py-2 text-sm">
                <div className="flex-1"><div className="font-medium">{i.name}</div><div className="text-xs text-muted-foreground">Sold {i.quantity} @ {money(i.unit_price)}</div></div>
                <Input type="number" min={0} max={i.quantity} value={qty[i.key] ?? ""} onChange={(e) => setQty({ ...qty, [i.key]: e.target.value })} placeholder="0" className="h-8 w-20 text-center" data-testid={`return-qty-${i.key}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Reason</Label>
              <Select value={reason} onValueChange={setReason}><SelectTrigger data-testid="return-reason-select"><SelectValue placeholder="Select reason" /></SelectTrigger><SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase text-muted-foreground">Refund via</Label>
              <Select value={refund} onValueChange={setRefund}><SelectTrigger data-testid="refund-method-select"><SelectValue /></SelectTrigger><SelectContent>{["cash", "card", "eft", "credit"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r === "credit" ? "Account credit" : r}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={restock} onChange={(e) => setRestock(e.target.checked)} data-testid="restock-checkbox" /> Return items to stock (untick if damaged/written off)</label>
          <div className="flex justify-between font-bold border-t border-border pt-3"><span>Credit note total (incl. VAT)</span><span className="tabular text-primary" data-testid="return-total">{money(shown)}</span></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} className="bg-primary hover:bg-[#C81219] text-white" data-testid="submit-return-button">Issue Credit Note</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
